/**
 * PriorityAiPanel — "Prioridad IA · Top 3".
 *
 * Text-only ranking, NOT ActionCards. Shows up to 3 entries with:
 *   rank · title · reason
 * plus a small badge for the urgency (colour + icon).
 *
 * Click on an item:
 *   1. If the target `action_id` is present in the visible compact
 *      board (dashboard case), scrolls to it and briefly highlights
 *      the card (via the `onFocusActionId` callback the parent wires).
 *   2. If it's not visible, resolves the primary CTA for the current
 *      role (resolvePrimaryCta) and navigates there — or does nothing
 *      if the CTA is not authorised.
 *
 * Never fetches its own data: the parent (dashboard) shares `items`
 * from `useActionCenterData` so we make only 2 requests per dashboard
 * mount (`GET /actions` + `POST /actions/next-best-action`).
 */
import React from "react";
import { useNavigate } from "react-router-dom";
import { Sparkle, ArrowRight, WarningOctagon, Clock, Warning, CalendarBlank, Info } from "@phosphor-icons/react";
import { useAuth } from "@/lib/auth";
import { resolvePrimaryCta } from "@/lib/actionCta";
import { urgencyStyleFor, urgencyLabel } from "@/components/actionCenter/urgencyStyle";

const ICONS_BY_KEY = { WarningOctagon, Clock, Warning, CalendarBlank, Info };

const I18N = {
  es: {
    eyebrow: "Prioridad IA",
    subtitle: "Las 3 acciones que priorizaría ahora.",
    empty: "Todo en orden.",
    emptySub: "No hay prioridades para hoy.",
    sourceAi: "⚡ Priorizado por Claude",
    sourceRules: "Ordenado por reglas",
    loading: "Calculando prioridades…",
  },
  en: {
    eyebrow: "AI Priority",
    subtitle: "The 3 actions I'd tackle first.",
    empty: "All clear.",
    emptySub: "No priorities right now.",
    sourceAi: "⚡ Prioritised by Claude",
    sourceRules: "Rule-based ordering",
    loading: "Computing priorities…",
  },
};

export default function PriorityAiPanel({
  nba = [],
  nbaSource = "empty",
  itemsByActionId,
  lang = "es",
  onFocusActionId,   // (action_id) => void — scroll+highlight in parent
  onEmpty,           // optional hook: called when there is nothing
  greeting,          // optional AI greeting
  loading = false,   // parent's fetch in flight: never claim "all clear" yet
}) {
  const t = I18N[lang] || I18N.es;
  const navigate = useNavigate();
  const { user } = useAuth();
  const role = user?.role || "admin";

  // Resolve each NBA entry to its corresponding action_item so we can
  // render title/urgency/description and dispatch clicks. NBA entries
  // without a matching item are dropped (defensive — backend already
  // validated).
  const resolved = (nba || [])
    .map((n) => {
      const item = itemsByActionId?.get?.(n.action_id);
      if (!item) return null;
      return {
        rank: n.rank,
        reason: n.reason,
        item,
      };
    })
    .filter(Boolean)
    .slice(0, 3);

  const showEmpty = resolved.length === 0;
  if (showEmpty && loading) {
    return (
      <div
        className="rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-950 p-4"
        data-testid="priority-ai-loading"
      >
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-stone-400">
          <Sparkle size={12} /> {t.eyebrow}
        </div>
        <div className="mt-2 text-sm text-stone-500 dark:text-stone-400">{t.loading}</div>
      </div>
    );
  }
  if (showEmpty) {
    if (typeof onEmpty === "function") onEmpty();
    return (
      <div
        className="rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-950 p-4"
        data-testid="priority-ai-empty"
      >
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-stone-400">
          <Sparkle size={12} /> {t.eyebrow}
        </div>
        <div className="mt-2 text-sm text-stone-700 dark:text-stone-300">
          {greeting || t.empty}
        </div>
        <div className="text-xs text-stone-500 dark:text-stone-400 mt-0.5">
          {t.emptySub}
        </div>
      </div>
    );
  }

  const handleClick = (entry) => {
    // Try to scroll+highlight if the parent gave us a hook.
    if (typeof onFocusActionId === "function") {
      const el = document.getElementById(`action-card-${entry.item.action_id}`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        onFocusActionId(entry.item.action_id);
        return;
      }
    }
    // Fall back to CTA navigation when the card is not in view.
    const cta = resolvePrimaryCta(entry.item, role);
    if (cta && cta.href) {
      navigate(cta.href);
    }
    // If neither: silently no-op (never invent a redirect).
  };

  return (
    <div
      className="rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-950 p-4"
      data-testid="priority-ai-panel"
    >
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-stone-400">
            <Sparkle size={12} /> {t.eyebrow}
          </div>
          <div className="text-xs text-stone-500 dark:text-stone-400 mt-0.5">
            {t.subtitle}
          </div>
        </div>
        <div className="text-[10px] text-stone-400">
          {nbaSource === "ai" ? t.sourceAi : nbaSource === "rules" ? t.sourceRules : ""}
        </div>
      </div>

      <ol className="mt-3 space-y-2">
        {resolved.map((entry) => {
          const style = urgencyStyleFor(entry.item.urgency);
          const Icon = ICONS_BY_KEY[style.iconKey] || Info;
          return (
            <li key={entry.item.action_id}>
              <button
                type="button"
                onClick={() => handleClick(entry)}
                className={`w-full text-left rounded-md p-2 min-h-[44px] flex items-start gap-3 border ${style.border} ${style.bg} hover:opacity-90 transition-opacity`}
                data-testid={`priority-ai-item-${entry.rank}`}
                data-action-id={entry.item.action_id}
              >
                <div className="flex flex-col items-center shrink-0">
                  <span className="text-[10px] text-stone-400 leading-none mt-1">
                    #{entry.rank}
                  </span>
                  <span className={`mt-1 ${style.color}`}>
                    <Icon size={14} weight="fill" />
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-stone-900 dark:text-stone-100 line-clamp-1">
                    {entry.item.title || "—"}
                  </div>
                  <div className="text-xs text-stone-600 dark:text-stone-400 line-clamp-2">
                    {entry.reason || entry.item.description || ""}
                  </div>
                  <div className={`text-[10px] uppercase tracking-wider mt-0.5 ${style.color}`}>
                    {urgencyLabel(entry.item.urgency, lang)}
                  </div>
                </div>
                <ArrowRight size={14} className="text-stone-400 shrink-0 mt-1" />
              </button>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
